import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const API_URL = "";

const defaultAcademyInfo = {
  history: "Founded in 1945, the academy has been a cornerstone of fine art education in San Antonio.",
  extendedHistory: {
    founder: { name: "Pompeo Luigi Coppini", years: "1870-1957", bio: "", imageUrl: "" },
    protege: { name: "Waldine Tauch", years: "1903-1986", bio: "", imageUrl: "" },
    milestones: []
  }
};

export const History = () => {
  const [academyInfo, setAcademyInfo] = useState(defaultAcademyInfo);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/academy-info`);
        if (response.ok) {
          setAcademyInfo(await response.json());
        }
      } catch (err) {
        console.error('Error fetching academy info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-stone-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="history-page">
      <SEOHead
        title="Our History"
        description="Explore the history of The Coppini Academy of Fine Art, founded in 1945 by sculptor Pompeo Coppini in San Antonio, TX. A 501(c)(3) nonprofit since 1952."
        canonicalPath="/about/history"
      />
      <section className="bg-stone-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif text-white mb-6">Our History</h1>
          <p className="text-xl text-stone-300">
            Over 80 years of preserving classical art traditions
          </p>
          <p className="text-stone-400 mt-4">
            <Link to="/about" className="text-cafa-gold hover:text-white underline">
              ← Back to About Us
            </Link>
          </p>
        </div>
      </section>

      {/* History Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-stone-700 leading-relaxed mb-12">
            {academyInfo.history}
          </p>
          
          {/* Founders Section */}
          {academyInfo.extendedHistory?.founder?.imageUrl && (
            <div className="mb-16">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">Our Founders</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-stone-50 rounded-lg shadow-sm overflow-hidden">
                  <div className="p-6 text-center">
                    <img 
                      src={academyInfo.extendedHistory.founder.imageUrl} 
                      alt={academyInfo.extendedHistory.founder.name}
                      className="w-48 h-64 object-contain mx-auto mb-4"
                    />
                    <h3 className="text-2xl font-serif text-stone-900 mb-2">
                      <Link to="/about/pompeo-coppini" className="hover:text-cafa-red transition-colors">
                        {academyInfo.extendedHistory.founder.name}
                      </Link>
                    </h3>
                    <p className="text-cafa-red font-medium mb-3">{academyInfo.extendedHistory.founder.years}</p>
                    <p className="text-stone-600 text-sm text-left">{academyInfo.extendedHistory.founder.bio}</p>
                  </div>
                </div>
                <div className="bg-stone-50 rounded-lg shadow-sm overflow-hidden">
                  <div className="p-6 text-center">
                    <img 
                      src={academyInfo.extendedHistory.protege.imageUrl} 
                      alt={academyInfo.extendedHistory.protege.name}
                      className="w-48 h-64 object-contain mx-auto mb-4"
                    />
                    <h3 className="text-2xl font-serif text-stone-900 mb-2">
                      <Link to="/about/waldine-tauch" className="hover:text-cafa-red transition-colors">
                        {academyInfo.extendedHistory.protege.name}
                      </Link>
                    </h3>
                    <p className="text-cafa-red font-medium mb-3">{academyInfo.extendedHistory.protege.years}</p>
                    <p className="text-stone-600 text-sm text-left">{academyInfo.extendedHistory.protege.bio}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          {academyInfo.extendedHistory?.milestones?.length > 0 && (
            <div className="mb-16">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">Academy Timeline</h2>
              <div className="bg-stone-50 p-8 rounded-lg">
                <div className="space-y-6">
                  {academyInfo.extendedHistory.milestones.map((milestone, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-20 text-right">
                        <span className="font-bold text-cafa-red text-lg">{milestone.year}</span>
                      </div>
                      <div className="flex-shrink-0 w-4 h-4 mt-1.5 rounded-full bg-cafa-red"></div>
                      <p className="text-stone-700">{milestone.event}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-12 border-t border-stone-200">
            <div className="text-center">
              <div className="text-5xl font-bold text-cafa-red mb-2">1945</div>
              <p className="text-stone-600 text-lg">Founded</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-cafa-red mb-2">80+</div>
              <p className="text-stone-600 text-lg">Years of Excellence</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-cafa-red mb-2">1000+</div>
              <p className="text-stone-600 text-lg">Students Trained</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif mb-4">Continue the Legacy</h2>
          <p className="text-stone-300 mb-8">
            Join us in preserving and advancing the tradition of classical fine art.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/membership"
              className="inline-flex items-center justify-center px-6 py-3 bg-cafa-red hover:bg-cafa-red-700 text-white rounded-md font-medium transition-colors"
            >
              Become a Member
            </Link>
            <Link 
              to="/classes/adult"
              className="inline-flex items-center justify-center px-6 py-3 border border-white text-white hover:bg-white/10 rounded-md font-medium transition-colors"
            >
              Explore Classes
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
